<template>
  <div class="org-management-container">
    <div class="org-header">
      <h2>Organization Management</h2>
      <p>Manage users and settings for {{ currentOrganization?.name }}</p>
    </div>
    
    <div class="tabs">
      <div 
        class="tab" 
        :class="{ 'active': activeTab === 'users' }"
        @click="activeTab = 'users'"
      >
        Users
      </div>
      <div 
        class="tab" 
        :class="{ 'active': activeTab === 'settings' }"
        @click="activeTab = 'settings'"
      >
        Settings
      </div>
    </div>
    
    <!-- Users Tab -->
    <div v-if="activeTab === 'users'" class="tab-content">
      <div class="section-header">
        <h3>Organization Users</h3>
        <button 
          v-if="isOrgAdmin" 
          class="primary-action-button" 
          @click="showInviteModal = true"
        >
          Invite User
        </button>
      </div>
      
      <div v-if="isLoading" class="loading">
        Loading users...
      </div>
      
      <div v-else-if="users.length === 0" class="no-data">
        No users found in this organization.
      </div>
      
      <div v-else class="users-list">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th v-if="isOrgAdmin">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.id">
              <td>{{ user.first_name }} {{ user.last_name }}</td>
              <td>{{ user.email }}</td>
              <td>
                <span v-if="!isEditing[user.id]">{{ user.role }}</span>
                <select 
                  v-else 
                  v-model="editedRole[user.id]"
                >
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                </select>
              </td>
              <td v-if="isOrgAdmin" class="actions">
                <div v-if="!isEditing[user.id]">
                  <button 
                    class="action-button edit"
                    @click="startEditing(user)"
                  >
                    Edit
                  </button>
                  <button 
                    class="action-button delete"
                    @click="confirmRemoveUser(user)"
                  >
                    Remove
                  </button>
                </div>
                <div v-else>
                  <button 
                    class="action-button save"
                    @click="saveUserRole(user)"
                  >
                    Save
                  </button>
                  <button 
                    class="action-button cancel"
                    @click="cancelEditing(user)"
                  >
                    Cancel
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    
    <!-- Settings Tab -->
    <div v-if="activeTab === 'settings'" class="tab-content">
      <div class="section-header">
        <h3>Organization Settings</h3>
      </div>
      
      <form @submit.prevent="updateOrganization" class="org-form">
        <div class="form-group">
          <label for="orgName">Organization Name</label>
          <input 
            id="orgName"
            type="text" 
            v-model="orgSettings.name" 
            placeholder="Organization Name"
            :disabled="!isOrgAdmin"
            required
          />
        </div>
        
        <div class="form-group">
          <label for="orgDescription">Description</label>
          <textarea 
            id="orgDescription"
            v-model="orgSettings.description" 
            placeholder="Organization description"
            :disabled="!isOrgAdmin"
            rows="3"
          ></textarea>
        </div>
        
        <div v-if="isOrgAdmin" class="form-actions">
          <button type="submit" class="primary-action-button">
            Save Changes
          </button>
        </div>
      </form>
    </div>
    
    <!-- Invite User Modal -->
    <div v-if="showInviteModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Invite User</h3>
          <button class="close-button" @click="showInviteModal = false">×</button>
        </div>
        
        <form @submit.prevent="inviteUser" class="invite-form">
          <div class="form-group">
            <label for="inviteEmail">Email Address</label>
            <input 
              id="inviteEmail"
              type="email" 
              v-model="inviteEmail" 
              placeholder="Enter email address"
              required
            />
          </div>
          
          <div class="form-group">
            <label for="inviteRole">Role</label>
            <select id="inviteRole" v-model="inviteRole" required>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>
          </div>
          
          <div class="form-actions">
            <button type="button" class="secondary-action-button" @click="showInviteModal = false">
              Cancel
            </button>
            <button type="submit" class="primary-action-button">
              Send Invitation
            </button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- Confirmation Modal -->
    <div v-if="showConfirmModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Confirm Action</h3>
          <button class="close-button" @click="showConfirmModal = false">×</button>
        </div>
        
        <div class="modal-body">
          <p>{{ confirmMessage }}</p>
        </div>
        
        <div class="modal-footer">
          <button class="secondary-action-button" @click="showConfirmModal = false">
            Cancel
          </button>
          <button class="primary-action-button danger" @click="confirmAction">
            Confirm
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, reactive } from 'vue';
import { useOrganizationStore } from "@/stores/organization_store";
import { useErrorStore } from "@/stores/error_store";
import { useSuccessStore } from "@/stores/success_store";

const organizationStore = useOrganizationStore();
const errorStore = useErrorStore();
const successStore = useSuccessStore();

// Tab management
const activeTab = ref('users');

// Organization data
const currentOrganization = computed(() => organizationStore.getCurrentOrganization);
const isOrgAdmin = computed(() => organizationStore.isOrgAdmin);
const isLoading = ref(false);

// Users management
const users = ref([]);
const isEditing = reactive({});
const editedRole = reactive({});

// Invite user form
const showInviteModal = ref(false);
const inviteEmail = ref('');
const inviteRole = ref('user');

// Organization settings form
const orgSettings = ref({
  name: '',
  description: ''
});

// Confirmation modal
const showConfirmModal = ref(false);
const confirmMessage = ref('');
const confirmCallback = ref(null);

// Load organization data on mount
onMounted(async () => {
  if (currentOrganization.value) {
    // Initialize organization settings
    orgSettings.value = {
      name: currentOrganization.value.name,
      description: currentOrganization.value.description || ''
    };
    
    // Load users
    await loadUsers();
  }
});

// Load organization users
const loadUsers = async () => {
  isLoading.value = true;
  
  try {
    const orgUsers = await organizationStore.getOrganizationUsers();
    users.value = orgUsers;
  } catch (error) {
    errorStore.setError('Error', 'Failed to load organization users', 'error');
  } finally {
    isLoading.value = false;
  }
};

// Start editing a user's role
const startEditing = (user) => {
  isEditing[user.id] = true;
  editedRole[user.id] = user.role;
};

// Cancel editing a user's role
const cancelEditing = (user) => {
  isEditing[user.id] = false;
};

// Save user role changes
const saveUserRole = async (user) => {
  isLoading.value = true;
  
  try {
    await organizationStore.updateUserRole(user.id, editedRole[user.id]);
    
    // Update the user in the local list
    const index = users.value.findIndex(u => u.id === user.id);
    if (index !== -1) {
      users.value[index].role = editedRole[user.id];
    }
    
    // Exit edit mode
    isEditing[user.id] = false;
    
    successStore.setSuccess('Success', 'User role updated successfully');
  } catch (error) {
    errorStore.setError('Error', 'Failed to update user role', 'error');
  } finally {
    isLoading.value = false;
  }
};

// Confirm removing a user
const confirmRemoveUser = (user) => {
  confirmMessage.value = `Are you sure you want to remove ${user.email} from this organization?`;
  confirmCallback.value = () => removeUser(user.id);
  showConfirmModal.value = true;
};

// Remove a user from the organization
const removeUser = async (userId) => {
  isLoading.value = true;
  
  try {
    await organizationStore.removeUser(userId);
    
    // Remove the user from the local list
    users.value = users.value.filter(user => user.id !== userId);
    
    successStore.setSuccess('Success', 'User removed from organization');
  } catch (error) {
    errorStore.setError('Error', 'Failed to remove user', 'error');
  } finally {
    isLoading.value = false;
    showConfirmModal.value = false;
  }
};

// Invite a new user to the organization
const inviteUser = async () => {
  isLoading.value = true;
  
  try {
    await organizationStore.inviteUser(inviteEmail.value, inviteRole.value);
    
    // Reset form and close modal
    inviteEmail.value = '';
    inviteRole.value = 'user';
    showInviteModal.value = false;
    
    // Reload users to include the new invitation
    await loadUsers();
    
    successStore.setSuccess('Success', 'Invitation sent successfully');
  } catch (error) {
    errorStore.setError('Error', 'Failed to send invitation', 'error');
  } finally {
    isLoading.value = false;
  }
};

// Update organization settings
const updateOrganization = async () => {
  isLoading.value = true;
  
  try {
    // This would need to be implemented in the organization store
    // await organizationStore.updateOrganization(orgSettings.value);
    
    successStore.setSuccess('Success', 'Organization settings updated successfully');
  } catch (error) {
    errorStore.setError('Error', 'Failed to update organization settings', 'error');
  } finally {
    isLoading.value = false;
  }
};

// Execute the confirmed action
const confirmAction = () => {
  if (confirmCallback.value) {
    confirmCallback.value();
  }
};
</script>

<style scoped lang="scss">
@import "./src/assets/main.scss";

.org-management-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.org-header {
  margin-bottom: 30px;
  
  h2 {
    margin-bottom: 5px;
    color: $darkGray1;
  }
  
  p {
    color: $mediumGray1;
  }
}

.tabs {
  display: flex;
  border-bottom: 1px solid $lightGray1;
  margin-bottom: 20px;
  
  .tab {
    padding: 10px 20px;
    cursor: pointer;
    font-weight: 500;
    color: $mediumGray1;
    
    &.active {
      color: $brightBlue1;
      border-bottom: 2px solid $brightBlue1;
    }
    
    &:hover:not(.active) {
      color: darken($mediumGray1, 15%);
    }
  }
}

.tab-content {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h3 {
    margin: 0;
    color: $darkGray1;
  }
}

.users-list {
  table {
    width: 100%;
    border-collapse: collapse;
    
    th, td {
      padding: 12px 15px;
      text-align: left;
      border-bottom: 1px solid $lightGray1;
    }
    
    th {
      font-weight: 600;
      color: $darkGray1;
      background-color: $darkWhite1;
    }
    
    tr:hover {
      background-color: rgba($brightBlue1, 0.05);
    }
    
    .actions {
      display: flex;
      gap: 10px;
    }
  }
}

.action-button {
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  border: none;
  
  &.edit {
    background-color: $lightBlue1;
    color: white;
  }
  
  &.delete {
    background-color: $red3;
    color: white;
  }
  
  &.save {
    background-color: $brightBlue1;
    color: white;
  }
  
  &.cancel {
    background-color: $lightGray1;
    color: $darkGray1;
  }
}

.form-group {
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
    color: $darkGray1;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 10px;
    border: 1px solid $lightGray1;
    border-radius: 4px;
    font-size: 14px;
    
    &:focus {
      border-color: $brightBlue1;
      outline: none;
    }
    
    &:disabled {
      background-color: $darkWhite1;
      cursor: not-allowed;
    }
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.primary-action-button {
  background-color: $brightBlue1;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background-color: darken($brightBlue1, 10%);
  }
  
  &.danger {
    background-color: $red3;
    
    &:hover {
      background-color: darken($red3, 10%);
    }
  }
}

.secondary-action-button {
  background-color: transparent;
  color: $brightBlue1;
  border: 1px solid $brightBlue1;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background-color: rgba($brightBlue1, 0.05);
  }
}

.loading {
  text-align: center;
  padding: 20px;
  color: $mediumGray1;
}

.no-data {
  text-align: center;
  padding: 30px;
  color: $mediumGray1;
  background-color: $darkWhite1;
  border-radius: 4px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid $lightGray1;
  
  h3 {
    margin: 0;
    color: $darkGray1;
  }
  
  .close-button {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: $mediumGray1;
    
    &:hover {
      color: $darkGray1;
    }
  }
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid $lightGray1;
}

.invite-form {
  padding: 20px;
}

@media (max-width: 768px) {
  .users-list {
    overflow-x: auto;
  }
  
  .form-actions {
    flex-direction: column;
    
    button {
      width: 100%;
      margin-bottom: 10px;
    }
  }
  
  .modal-content {
    width: 90%;
  }
}
</style>